import LoginUtils from "@/utils/login/LoginUtils.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import CapabilityType from "@/utils/constants/CapabilityType.ts";
import EditAuthority from "@/utils/constants/EditAuthority.ts";
import PrintAuthority from "@/utils/constants/PrintAuthority.ts";
import DomainBaseFieldIDs from "@/utils/constants/DomainBaseFieldIDs.ts";
import DTOSecurityFieldAuthority from "@/utils/constants/DTOSecurityFieldAuthority";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import ApplicableWhen from "@/utils/constants/ApplicableWhen.ts";
import TrueFalseNotSpecified from "@/utils/constants/TrueFalseNotSpecified.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import DTOUser = namaTypes.DTOUser;
import DTOActionSecurityLine = namaTypes.DTOActionSecurityLine;

export default class SecurityUtils {
  static userCan(entityType: string | undefined, capability: string) {
    if (!entityType)
      return false;
    if (!LoginUtils.loginInfo || !LoginUtils.loginInfo.loggedIn)
      return false;
    return ClientCache.isEntityEnabled(entityType) && this.userCanConsiderAlternative(entityType, capability);
  }

  static userCanAll(entityType: string | undefined, securityCapabilities: string[]): boolean {
    if (!securityCapabilities)
      return true;
    for (let capability of securityCapabilities)
      if (!this.userCan(entityType, capability))
        return false;
    return true;
  }

  static userCanConsiderAlternative(entityType: string, capability: string) {
    if (this.isMandatoryCapability(entityType, capability))
      return true;
    let currentUserCan = this.userCan_(entityType, capability, LoginUtils.loginInfo!.userInfo);
    if (currentUserCan)
      return currentUserCan;
    let alternativeUsers = LoginUtils.loginInfo!.userInfo!.alternativeUsers;
    for (let user of alternativeUsers ?? []) {
      if (this.userCan_(entityType, capability, user))
        return true;
    }
    return false;
  }

  static isMandatoryCapability(entityType: string | null | undefined, capability: string) {
    return ObjectChecker.isAnyEqualToFirst(entityType, DomainBaseEntities.DocumentBook, DomainBaseEntities.DocumentTerm,
        DomainBaseEntities.MasterGroup, BSCEntities.Currency)
      && ObjectChecker.isAnyEqualToFirst(capability, CapabilityType.ListView, CapabilityType.RecordView, CapabilityType.SearchView);
  }

  static userCan_(entityType: string, capabilityId: string, user: namaTypes.DTOUser | undefined) {
    if (!user || !user.fullProfile || !user.fullProfile.standardLines)
      return false;
    if (user.fullProfile.fullAuthority)
      return true;
    if (!entityType || !capabilityId)
      return true;
    let userCan = this.internalUserCan(entityType, capabilityId, user.settings?.standardLines);
    if (userCan !== null)
      return userCan;
    userCan = this.internalUserCan(entityType, capabilityId, user.fullProfile.standardLines);
    if (userCan !== null)
      return userCan;
    return false;
  }

  static userCanDeleteDraft(entityType: string): boolean {
    return this.userCan(entityType, CapabilityType.Delete) || this.userCan(entityType, CapabilityType.DeleteDraft);
  }

  static userCanDelete(entityType: string): boolean {
    return this.userCan(entityType, CapabilityType.Delete);
  }

  static userCanApprove(entityType: string): boolean {
    return this.currentUserHasEmployee() && this.userCan(entityType, CapabilityType.Approve);
  }

  private static currentUserHasEmployee(): boolean {
    if (LoginUtils.loginInfo && LoginUtils.loginInfo.loggedIn && LoginUtils.loginInfo.userInfo && LoginUtils.loginInfo.userInfo.employee)
      return true;
    return false;
  }

  static internalUserCan(entityType: string, capabilityId: string, standardLines: namaTypes.DTOUserStandardSecurityLine[] | undefined) {
    if (!standardLines || !standardLines.length)
      return null;
    for (let userCapability of standardLines) {
      if (userCapability.targetEntity === entityType)
        return this.userCanCapability(userCapability, capabilityId);
    }
    for (let userCapability of standardLines) {
      if (this.entityTypeListContainsEntity(userCapability.targetEntities, entityType))
        return this.userCanCapability(userCapability, capabilityId);
    }
    for (let userCapability of standardLines) {
      if (ObjectChecker.areAllEmptyOrNull(userCapability.targetEntities, userCapability.targetEntity))
        return this.userCanCapability(userCapability, capabilityId);
    }
    return null;
  }

  static userCanCapability(userCapability: namaTypes.DTOUserStandardSecurityLine, capabilityId: string) {
    if (capabilityId === CapabilityType.Commit)
      return this.canCommit(userCapability);
    if (capabilityId === CapabilityType.CreateDraft)
      return userCapability.canEdit === EditAuthority.SaveDraft || this.canCommit(userCapability);
    if (capabilityId === CapabilityType.SaveDraft)
      return (userCapability.canEdit === EditAuthority.SaveDraft || this.canCommit(userCapability)) && !userCapability.preventSaveDraft;
    if (capabilityId === CapabilityType.Delete)
      return userCapability.canDelete;
    if (capabilityId === CapabilityType.DeleteDraft) {
      if ("Yes" === userCapability.draftDeletionCapability)
        return true;
      else if ("No" === userCapability.draftDeletionCapability)
        return false;
      return userCapability.canDelete;
    }
    if (capabilityId === CapabilityType.ChangeCapability)
      return userCapability.canChangeCapability || userCapability.canEdit === EditAuthority.EditAfterCommit;
    if (capabilityId === CapabilityType.EditAfterCommit)
      return userCapability.canEdit === EditAuthority.EditAfterCommit;
    if (capabilityId === CapabilityType.ListView)
      return userCapability.listView;
    if (capabilityId === CapabilityType.SearchView)
      return userCapability.searchView || userCapability.listView;
    if (capabilityId === CapabilityType.PrintMoreThanOnce)
      return userCapability.canPrint === PrintAuthority.MoreThanOne;
    if (capabilityId === CapabilityType.PrintOnce)
      return userCapability.canPrint === PrintAuthority.One || userCapability.canPrint === PrintAuthority.MoreThanOne;
    if (capabilityId === CapabilityType.RecordView)
      return userCapability.recordView;
    if (capabilityId === CapabilityType.Approve)
      return true;
    if (capabilityId === CapabilityType.Revise)
      return userCapability.canRevise;
    if (capabilityId === CapabilityType.Unrevise)
      return userCapability.canUnRevise;
    if (capabilityId === CapabilityType.Duplicate)
      return userCapability.canDuplicate;
    if (capabilityId === CapabilityType.Export)
      return userCapability.canExport;
    if (capabilityId === CapabilityType.Import)
      return userCapability.canImport;
    if (capabilityId.startsWith(CapabilityType.Revise)) {
      return this.canPerformOnLevel(capabilityId.replaceAll(CapabilityType.Revise, ""), userCapability.reviseLevels, userCapability.canRevise);
    } else if (capabilityId.startsWith(CapabilityType.Unrevise)) {
      return this.canPerformOnLevel(capabilityId.replaceAll(CapabilityType.Unrevise, ""), userCapability.unreviseLevels, userCapability.canUnRevise);
    }
    if (capabilityId === CapabilityType.CanViewTransaction)
      return !userCapability.preventViewSystemTransaction;
    if (capabilityId === CapabilityType.CanViewMoreMenu)
      return !userCapability.preventViewMoreMenu;
    return false;
  }

  static canCommit(userCapability: namaTypes.DTOUserStandardSecurityLine) {
    return userCapability.canEdit === EditAuthority.Commit || userCapability.canEdit === EditAuthority.EditAfterCommit;
  }

  static canPerformOnLevel(levelStr: string, allowedLevels: string | undefined, userCan: boolean | undefined) {
    if (userCan === false)
      return false;
    if (!allowedLevels)
      return true;
    let level = 0;
    if (levelStr)
      level = parseInt(levelStr.toUpperCase().replace("L", ""));
    let sep = ",";
    if (!allowedLevels.includes(sep))
      sep = "-";
    let allowedLevelsList = allowedLevels.toUpperCase().replace("L", "").split(sep);
    for (let allowedLevelStr of allowedLevelsList) {
      let allowedLevel = parseInt(allowedLevelStr);
      if (allowedLevel === level)
        return true;
    }
    return false;
  }

  static entityTypeListContainsEntity(authorityEntityTypeList: namaTypes.EntityReferenceData | undefined, targetEntity: string) {
    if (!authorityEntityTypeList)
      return false;
    let entityTypes = authorityEntityTypeList.extraData ? authorityEntityTypeList.extraData[DomainBaseFieldIDs.EntityTypeList_lines_entityType] : "";
    return entityTypes.indexOf("," + targetEntity + ",") >= 0;
  }

  static sameEntity(targetEntity: string, authorityEntityType: string | undefined) {
    return targetEntity === authorityEntityType;
  }

  static getPageAuthority(entityType: string, resourceId: string | undefined, pageIndex: number) {
    if (!LoginUtils.loginInfo)
      return;
    let user: namaTypes.DTOUser = LoginUtils.loginInfo.userInfo!;
    let pageAuthority: string | undefined = this.getPageAuthorityForUser(user, entityType, resourceId, pageIndex);
    if (ObjectChecker.areEqual(pageAuthority, DTOSecurityFieldAuthority.Normal))
      return pageAuthority;
    let currentHighPageAuthority: string | undefined;
    for (let alternateUser of user.alternativeUsers ?? []) {
      currentHighPageAuthority = this.findHigherAuthority(pageAuthority, this.getPageAuthorityForUser(alternateUser, entityType, resourceId, pageIndex))
      if (ObjectChecker.areEqual(currentHighPageAuthority, DTOSecurityFieldAuthority.Normal))
        return currentHighPageAuthority;
      pageAuthority = currentHighPageAuthority;
    }
    return ObjectChecker.getFirstNotEmptyObj(currentHighPageAuthority, pageAuthority);
  }

  static findHigherAuthority(pageAuthority: string | undefined, alternateAuthority: string | undefined) {
    if (this.shouldBeIgnored(alternateAuthority))
      return pageAuthority;
    if (ObjectChecker.areEqual(alternateAuthority, DTOSecurityFieldAuthority.Normal))
      return alternateAuthority;
    if (ObjectChecker.areEqual(pageAuthority, DTOSecurityFieldAuthority.Disabled))
      return pageAuthority;
    return alternateAuthority;
  }

  static shouldBeIgnored(authority: string | undefined) {
    return ObjectChecker.isEmptyOrNull(authority) || ObjectChecker.areEqual(authority, DTOSecurityFieldAuthority.NotDefined);
  }

  static getPageAuthorityForUser(user: namaTypes.DTOUser, targetEntity: string, pageId: string | undefined, pageIndex: number) {
    if (user.fullProfile == null)
      return DTOSecurityFieldAuthority.Normal;
    if (targetEntity == null)
      return DTOSecurityFieldAuthority.Normal;
    if (user.fullProfile.fullAuthority === true)
      return DTOSecurityFieldAuthority.Normal;
    if (ObjectChecker.areAllEmptyOrNull(user.settings?.pageSecurity, user.fullProfile.pageSecurity))
      return DTOSecurityFieldAuthority.NotDefined;
    let pageSecurity: string | undefined = this.calcPageSecurity(targetEntity, pageId, pageIndex, user.settings?.pageSecurity);
    if (ObjectChecker.areNotEqual(pageSecurity, DTOSecurityFieldAuthority.NotDefined))
      return pageSecurity;
    return this.calcPageSecurity(targetEntity, pageId, pageIndex, user.fullProfile.pageSecurity);
  }

  static calcPageSecurity(targetEntity: string, pageId: string | undefined, pageNumber: number, lines: namaTypes.DTOPageSecurity[] | undefined) {
    if (ObjectChecker.isEmptyOrNull(lines))
      return DTOSecurityFieldAuthority.NotDefined;
    for (let authority of lines || []) {
      if ((this.sameEntity(targetEntity, authority.entityType) || this.entityTypeListContainsEntity(authority.entityTypeList, targetEntity)
        || ObjectChecker.areAllEmptyOrNull(authority.entityType, authority.entityTypeList)) && (
        ObjectChecker.areEqual(pageId, authority.pageName) ||
        ObjectChecker.areEqual("" + (pageNumber + 1), authority.pageName)))
        return authority.authorityType;
    }
    return DTOSecurityFieldAuthority.NotDefined;
  }

  static userCanView(entityType: string, fieldId: string, applicableWhen: string): boolean {
    if (!useLoginInfoStore().isLoggedIn)
      return false;
    return ClientCache.isEntityEnabled(entityType) && this.userCanViewConsiderAlternative(entityType, fieldId, applicableWhen);
  }

  static userCanViewConsiderAlternative(entityType: string, fieldId: string, applicableWhen: string): boolean {
    let userInfo = LoginUtils.loginInfo?.userInfo!;
    let canViewField = this.userCanViewFieldTriState(userInfo, entityType, fieldId, applicableWhen);
    if (canViewField !== TrueFalseNotSpecified.NotSpecified)
      return canViewField === TrueFalseNotSpecified.True;
    for (let alternativeUser of userInfo.alternativeUsers ?? []) {
      let canView = this.userCanViewFieldTriState(alternativeUser, entityType, fieldId, applicableWhen);
      if (canView !== TrueFalseNotSpecified.NotSpecified)
        return canView === TrueFalseNotSpecified.True;
    }
    return true;
  }

  static userCanViewFieldTriState(userInfo: namaTypes.DTOUser | undefined, targetEntity: string, fieldId: string, applicableWhen: string): TrueFalseNotSpecified {
    if (!userInfo || !userInfo.fullProfile)
      return TrueFalseNotSpecified.False;
    if (!userInfo.settings?.disabledFields && !userInfo.fullProfile.disabledFields)
      return TrueFalseNotSpecified.NotSpecified;
    if (!targetEntity || !fieldId)
      return TrueFalseNotSpecified.NotSpecified;
    if (userInfo.fullProfile.fullAuthority)
      return TrueFalseNotSpecified.True;
    let authority = this.getFieldSecurityLine(userInfo, targetEntity, fieldId, applicableWhen);
    if (authority == null)
      return TrueFalseNotSpecified.NotSpecified;
    return authority.authorityType !== DTOSecurityFieldAuthority.InVisible ? TrueFalseNotSpecified.True : TrueFalseNotSpecified.False;
  }

  static userCanViewField(userInfo: namaTypes.DTOUser | undefined, targetEntity: string, fieldId: string, applicableWhen: string) {
    if (!userInfo)
      return false;
    if (!userInfo.fullProfile)
      return false;
    if (!userInfo.settings?.disabledFields && !userInfo.fullProfile.disabledFields)
      return true;
    if (!targetEntity || !fieldId)
      return true;
    if (userInfo.fullProfile.fullAuthority)
      return true;
    let authority = this.getFieldSecurityLine(userInfo, targetEntity, fieldId, applicableWhen);
    if (authority == null)
      return true;
    return authority.authorityType !== DTOSecurityFieldAuthority.InVisible;
  }

  static getFieldSecurityLine(userInfo: namaTypes.DTOUser, targetEntity: string, fieldId: string, applicableWhen: string) {
    let line = this.calcFieldLineFromLines(targetEntity, fieldId, applicableWhen, userInfo.settings?.disabledFields ?? []);
    if (line != null)
      return line;
    return this.calcFieldLineFromLines(targetEntity, fieldId, applicableWhen, userInfo.fullProfile!.disabledFields ?? []);
  }

  private static calcFieldLineFromLines(targetEntity: string, fieldId: string, applicableWhen: string, disabledFields: namaTypes.DTOSecurityFieldAuthority[] | undefined) {
    if (!disabledFields)
      return null;
    let wildCardLine: namaTypes.DTOSecurityFieldAuthority | null = null;
    for (let authority of disabledFields) {
      if (this.modeApplicable(authority, applicableWhen) && this.sameEntity(targetEntity, authority.entityType)) {
        if (fieldId === authority.fieldId)
          return authority;
        if (wildCardLine == null && "*" === authority.fieldId)
          wildCardLine = authority;
      }
    }
    for (let authority of disabledFields) {
      if (this.modeApplicable(authority, applicableWhen) && this.entityTypeListContainsEntity(authority.targetEntities, targetEntity)) {
        if (fieldId === authority.fieldId)
          return authority;
        if (wildCardLine == null && "*" === authority.fieldId)
          wildCardLine = authority;
      }
    }
    if (wildCardLine == null) {
      for (let authority of disabledFields) {
        if (this.modeApplicable(authority, applicableWhen) && !authority.targetEntities && !authority.entityType) {
          if (fieldId === authority.fieldId)
            return authority;
          if (wildCardLine == null && "*" === authority.fieldId)
            wildCardLine = authority;
        }
      }
    }
    return wildCardLine;
  }

  static modeApplicable(authority: namaTypes.DTOSecurityFieldAuthority, applicableWhen: string) {
    switch (applicableWhen) {
      case ApplicableWhen.NewMode:
        return authority.applicableWithNewMode;
      case ApplicableWhen.UpdateMode:
        return authority.applicableWithUpdateMode;
      case ApplicableWhen.DraftMode:
        return authority.applicableWithDraftMode;
    }
    return authority.applicableWithNewMode && authority.applicableWithUpdateMode && authority.applicableWithDraftMode;
  }

  static userCanEdit(entityType: string, fieldId?: string, applicableWhen?: string) {
    if (!fieldId && !applicableWhen)
      return this.userCan(entityType, CapabilityType.Commit);
    if (ClientCache.isDisabledField(entityType, fieldId))
      return false;
    if (!useLoginInfoStore().isLoggedIn)
      return false;
    return ClientCache.isEntityEnabled(entityType) && this.userCanEditConsiderAlternative(entityType, fieldId!, applicableWhen!);
  }

  static userCanEditConsiderAlternative(entityType: string, fieldId: string, applicableWhen: string) {
    let user = LoginUtils.loginInfo?.userInfo!;
    let canEditField = this.userCanEditField(user, entityType, fieldId, applicableWhen);
    if (canEditField !== TrueFalseNotSpecified.NotSpecified)
      return canEditField === TrueFalseNotSpecified.True;
    for (let alternativeUser of user.alternativeUsers ?? []) {
      let canEdit = this.userCanEditField(alternativeUser, entityType, fieldId, applicableWhen);
      if (canEdit !== TrueFalseNotSpecified.NotSpecified)
        return canEdit === TrueFalseNotSpecified.True;
    }
    return true;
  }

  static userCanEditField(user: DTOUser, targetEntity: string, fieldId: string, applicableWhen: string) {
    if (!user.fullProfile)
      return TrueFalseNotSpecified.False;
    if ((!user.settings || !user.settings.disabledFields) && !user.fullProfile.disabledFields)
      return TrueFalseNotSpecified.NotSpecified;
    if (user.fullProfile.fullAuthority)
      return TrueFalseNotSpecified.True;
    let authority = this.getFieldSecurityLine(user, targetEntity, fieldId, applicableWhen);
    if (!authority)
      return TrueFalseNotSpecified.NotSpecified;
    return authority.authorityType !== DTOSecurityFieldAuthority.Disabled ? TrueFalseNotSpecified.True : TrueFalseNotSpecified.False;
  }

  static userCanCreate(entityType: string) {
    return this.userCan(entityType, CapabilityType.CreateDraft);
  }

  static userCanFeature(relatedToFeature ?: string): boolean {
    if (!relatedToFeature)
      return true;
    if (relatedToFeature.startsWith("m:"))
      return ClientCache.isModuleLoaded(relatedToFeature.substring(2));
    if (relatedToFeature.startsWith("e:"))
      return NamaCommon.binarySearch(ClientCache.getAllEntityTypes(), relatedToFeature.substring(2)) >= 0;
    return NamaCommon.binarySearch(ClientCache.getSortedDisbaledFeatures(), relatedToFeature) < 0;
  }

  static userCanAnyFeature(relatedToFeatures: (string | undefined)[]): boolean {
    if (!relatedToFeatures)
      return true;
    for (let feature of relatedToFeatures) {
      if (this.userCanFeature(feature))
        return true;
    }
    return false;
  }

  static userCanAction(entityType: string | undefined, actionId: string | undefined): boolean {
    if (!entityType)
      return false;
    const loginInfoStore = useLoginInfoStore();
    if (!loginInfoStore.isLoggedIn)
      return false;
    let userInfo: DTOUser = loginInfoStore.loginInfo!.userInfo!;
    let userCanAction: boolean = this.checkUserAuthorityForAction(userInfo, entityType, actionId);
    if (userCanAction)
      return userCanAction;
    for (let user of userInfo?.alternativeUsers ?? []) {
      if (this.checkUserAuthorityForAction(user, entityType, actionId))
        return true;
    }
    return false;
  }

  private static checkUserAuthorityForAction(userInfo: DTOUser, entityType: string, actionId: string | undefined) {
    if (!userInfo.fullProfile || !userInfo.fullProfile.standardLines)
      return false;
    if (entityType == null)
      return false;
    if (userInfo.fullProfile.fullAuthority || !actionId)
      return true;
    let line: DTOActionSecurityLine | null = this.getActionSecurityLine(userInfo, entityType, actionId);
    if (line)
      return ObjectChecker.areEqual(line.authorityType, "enabled");
    return true;
  }

  static getActionSecurityLine(userInfo: DTOUser, targetEntity: string, actionId: string): DTOActionSecurityLine | null {
    let wildCardLine: DTOActionSecurityLine | null = null;
    for (let authority of userInfo.fullProfile?.actionLines ?? []) {
      if (ObjectChecker.areEqual(targetEntity, authority.targetEntity)) {
        if (ObjectChecker.areEqual(actionId, authority.actionId))
          return authority;
        if (!wildCardLine && ObjectChecker.areEqual("*", authority.actionId))
          wildCardLine = authority;
      }

    }
    for (let authority of userInfo.fullProfile?.actionLines ?? []) {
      if (this.entityTypeListContainsEntity(authority.targetEntities, targetEntity)) {
        if (ObjectChecker.areEqual(actionId, authority.actionId))
          return authority;
        if (!wildCardLine && ObjectChecker.areEqual("*", authority.actionId))
          wildCardLine = authority;
      }
    }
    if (!wildCardLine) {
      for (let authority of userInfo.fullProfile?.actionLines ?? []) {
        if (ObjectChecker.areAllEmptyOrNull(authority.targetEntities, authority.targetEntity)) {
          if (ObjectChecker.areEqual(actionId, authority.actionId))
            return authority;
          if (!wildCardLine && ObjectChecker.areEqual("*", authority.actionId))
            wildCardLine = authority;
        }
      }
    }
    return wildCardLine;
  }

  static isUserLimitedToASingleLegalEntity() {
    return false;
  }
}
